import { isEmpty } from 'lodash';
import moment from 'moment';
import { useState, useCallback, useEffect, useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { AttachedFile } from '@hivebrite/design-system/AttachedFile';
import { Avatar } from '@hivebrite/design-system/Avatar';
import { Box } from '@hivebrite/design-system/Box';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { LinkSimple, FakeLink } from '@hivebrite/design-system/Link';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { linkEffectForChildren } from '@hivebrite/design-system/Theme';
import { onEnterOrSpacePress } from '@hivebrite/utils';

import AttachedEmbed from 'hb-react/frontoffice/components/AttachedEmbed';
import ImagePreviewModal from 'hb-react/frontoffice/components/ImagePreviewModal';
import mentionsOptions from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/mentionsOptions';
import UsersModal from 'hb-react/shared/components/DesignSystem/UsersModal';
import { HtmlContent } from 'hb-react/shared/components/HtmlContent';
import { Translation } from 'hb-react/shared/components/Translation';
import { fullName } from 'helpers/name';

import ActionItems from './ActionItems';
import { CommentForm } from './CommentForm';
import { VerticalDivider } from './VerticalDivider';
import Votes from './Votes';
import { createLike, destroyLike } from '../utils/actions';
import {
  serializeImageFileForRender,
  serializeFileForRender,
  prepareImagePreviews,
} from '../utils/uploads';

const SimpleComment = ({
  canDisplayEmojiPicker,
  comment,
  currentUser,
  options,
  isReply,
  onDeleteComment,
  onReply,
  onToggleLike,
  onUpdateComment,
  onVote,
}) => {
  const {
    attachments,
    content_html: contentHtml,
    created_at: createdAt,
    likes: initialLikes,
    user,
    uploads,
    replies,
    embed,
    votes,
  } = comment;
  const [likes, setLikes] = useState(initialLikes);
  const [isEdition, setIsEdition] = useState(false);
  const [isModalImagePreviewOpen, setIsModalImagePreviewOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [itemToReport, setItemToReport] = useState();

  const FAKE_ID = -1;
  const FAKE_ID_DELETE = -2;

  const isThereAnyReply = Boolean(replies && replies.total > 0);

  const attachedFiles = useMemo(
    () => attachments?.map(serializeFileForRender),
    [attachments],
  );
  const attachedImage = useMemo(
    () =>
      uploads?.length ? serializeImageFileForRender(uploads[0]) : undefined,
    [uploads],
  );
  const attachedImagesPreview = useMemo(
    () => (attachedImage ? prepareImagePreviews([attachedImage]) : undefined),
    [attachedImage],
  );

  const onLikeSuccess = useCallback(
    (like, draftLikes) => {
      const confirmedLikes = { ...draftLikes, id: like.id };

      setLikes(confirmedLikes);

      onToggleLike({ newLikes: confirmedLikes, commentId: comment.id });
    },
    [setLikes, onToggleLike, comment],
  );

  const onLikeError = useCallback(() => {
    setLikes((prev) => ({ ...prev, count: prev.count - 1, id: null }));
  }, [setLikes]);

  const onUnLikeSuccess = useCallback(
    (draftLikes) => {
      const confirmedLikes = { ...draftLikes, id: null };

      setLikes(confirmedLikes);

      onToggleLike({ newLikes: confirmedLikes, commentId: comment.id });
    },
    [setLikes, onToggleLike, comment],
  );

  const onUnLikeError = useCallback(() => {
    setLikes((prev) => ({
      ...prev,
      count: prev.count + 1,
      id: initialLikes.id,
    }));
  }, [setLikes, initialLikes]);

  const handleClickOnLike = useCallback(() => {
    if (likes?.id) {
      const draftLikes = {
        ...likes,
        count: likes.count - 1,
        id: FAKE_ID_DELETE,
      };

      setLikes(draftLikes);

      destroyLike({
        draftLikes,
        likeableType: 'Comment',
        likeId: likes.id,
        onResponse: onUnLikeSuccess,
        onError: onUnLikeError,
      });
    } else {
      const draftLikes = { ...likes, count: likes.count + 1, id: FAKE_ID };

      setLikes(draftLikes);

      createLike({
        draftLikes,
        likeableId: comment.id,
        likeableType: 'Comment',
        onResponse: onLikeSuccess,
        onError: onLikeError,
        userId: currentUser.id,
      });
    }
  }, [
    likes,
    setLikes,
    currentUser,
    comment,
    onLikeSuccess,
    onLikeError,
    onUnLikeSuccess,
    onUnLikeError,
    FAKE_ID,
    FAKE_ID_DELETE,
  ]);

  const handleEdition = () => {
    setIsEdition(true);
  };

  const handleCloseEdition = () => {
    setIsEdition(false);
  };

  const handleReply = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isReply && user.id !== currentUser.id) {
      onReply({
        mention: { name: user.name, id: user.id },
        char: mentionsOptions.chars[0],
      });
    } else {
      onReply();
    }
  };

  const handleReportItem = useCallback(() => {
    setItemToReport(comment);
  }, [comment, setItemToReport]);

  const handleCloseReportContent = useCallback(() => {
    setItemToReport(null);
  }, [setItemToReport]);

  const handleOpenModalImagePreview = () => setIsModalImagePreviewOpen(true);
  const handleCloseModalImagePreview = () => setIsModalImagePreviewOpen(false);

  useEffect(() => {
    if (initialLikes) {
      setLikes(initialLikes);
    }
  }, [initialLikes]);

  const isUserCommentAuthor = user?.id === currentUser?.id;
  const isAuthorCommentInEditMode = isEdition && isUserCommentAuthor;
  const colorFakeLikeIdDelete =
    likes?.id === FAKE_ID_DELETE ? 'dark' : 'primary';
  const iconTypeFakeLikeIdDelete =
    likes?.id === FAKE_ID_DELETE ? 'regular' : 'solid';
  const colorLikes = likes?.id ? colorFakeLikeIdDelete : 'dark';
  const iconTypeLikes = likes?.id ? iconTypeFakeLikeIdDelete : 'regular';
  const canClickOnLikes = !(
    likes?.id === FAKE_ID || likes?.id === FAKE_ID_DELETE
  );

  const renderContent = ({ toggle, isOpen, translatedContentHtml } = {}) => {
    const customContentHtml = translatedContentHtml || contentHtml;

    return (
      <>
        <CommentWrapper>
          <Box justifyContent="space-between">
            <UserDataWrapper>
              {user && (
                <Dotdotdot clamp={1} useNativeClamp>
                  <LinkSimpleWrapper to={Routes.user_path(user?.id)}>
                    <UserName>{fullName(user)}</UserName>
                  </LinkSimpleWrapper>
                </Dotdotdot>
              )}
              {createdAt && (
                <CommentTimeStamp font="metadata-regular-asphalt" pt="2px">
                  {moment(createdAt).fromNow()}
                </CommentTimeStamp>
              )}
            </UserDataWrapper>
            {user && (
              <Box ml={3}>
                <ActionItems
                  commentUser={user}
                  currentUser={currentUser}
                  id={comment.id}
                  isReply={isReply}
                  itemToReport={itemToReport}
                  onCloseReportContent={handleCloseReportContent}
                  onDeleteComment={onDeleteComment}
                  onEditComment={handleEdition}
                  onReportItem={handleReportItem}
                />
              </Box>
            )}
          </Box>
          {customContentHtml && (
            <CommentContentWrapper>
              <HtmlContent content={customContentHtml} />
            </CommentContentWrapper>
          )}
          {Boolean(attachedFiles?.length) && (
            <Box mb={1} flexDirection="column">
              {attachedFiles.map(({ id, ...fileProps }) => (
                <Box key={id} flexDirection="column" mt={1}>
                  <AttachedFile file={fileProps} size="small" withLink />
                </Box>
              ))}
            </Box>
          )}
          <AttachedEmbed embed={embed} />
          {attachedImage && (
            <Box
              mt={!isEmpty(embed) ? 4 : 0}
              data-testid="comment-attached-image"
            >
              <ImgStyled
                src={attachedImage.thumbnailUrl}
                alt=""
                onClick={handleOpenModalImagePreview}
              />
            </Box>
          )}
        </CommentWrapper>
        <Box
          alignItems="center"
          mt={attachedImage?.fileUrl ? 4 : 2}
          fontSize={12}
          pl={2}
        >
          {options?.votes?.enableVotes && votes && (
            <Votes
              onSuccess={options.votes.onSuccess}
              onVote={onVote}
              voteableId={comment.id}
              voteableType="Comment"
              votes={votes}
            />
          )}

          <ContentWithDotAfter $enabled={!!likes?.count}>
            <CommentLikesFakeLink
              data-testid="like-comment"
              $enabled={canClickOnLikes}
              onClick={canClickOnLikes ? handleClickOnLike : undefined}
              onKeyDown={
                canClickOnLikes
                  ? onEnterOrSpacePress(handleClickOnLike)
                  : undefined
              }
              color={colorLikes}
              tabIndex={0}
            >
              {__('Like')}
            </CommentLikesFakeLink>
          </ContentWithDotAfter>

          {likes?.count > 0 && (
            <LinkCounterLink
              $count={likes?.count || 0}
              onClick={() =>
                likes?.count > 0 ? setIsUsersModalOpen(true) : undefined
              }
              styledOnlyOnHover
            >
              <Box color={colorLikes} alignItems="start">
                <FontAwesomeIcon
                  iconName="heart"
                  iconType={iconTypeLikes}
                  height="12px"
                />
                <ScreenReadersContent>{__('Heart icon')}</ScreenReadersContent>
              </Box>
              <Box ml={1} fontSize={12} data-testid="comment-like-count">
                {likes.count}
              </Box>
            </LinkCounterLink>
          )}
          <VerticalDivider mx="6px" />
          <FakeLink
            onClick={handleReply}
            styledOnlyOnHover
            data-testid="reply-comment"
          >
            <CommentFakeLink>{__('Reply')}</CommentFakeLink>
          </FakeLink>
          {!isReply && isThereAnyReply && (
            <ContentWithDotBefore fontSize={12}>
              {n__(
                '%{replies} reply',
                '%{replies} replies',
                replies?.total,
              ).i__({
                replies: replies?.total,
              })}
            </ContentWithDotBefore>
          )}
          {toggle && (
            <>
              <VerticalDivider mx="6px" />
              <CommentFakeLink
                onClick={toggle}
                onKeyDown={onEnterOrSpacePress(toggle)}
              >
                {isOpen
                  ? s__('Translations|See original')
                  : s__('Translations|See translation')}
              </CommentFakeLink>
            </>
          )}
        </Box>
      </>
    );
  };

  return (
    <Box flexDirection="column" width="100%">
      {isAuthorCommentInEditMode && comment ? (
        <CommentForm
          autoFocus
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          initialComment={comment}
          isReply={isReply}
          onCloseEdition={handleCloseEdition}
          onComment={onUpdateComment}
          user={currentUser}
        />
      ) : (
        <>
          <Box>
            <Box>
              {user && (
                <LinkSimple to={Routes.user_path(user?.id)}>
                  <Avatar
                    alt=""
                    src={user?.photo_thumb_url}
                    width="32px"
                    height="32px"
                  />
                  <ScreenReadersContent>{__('Avatar')}</ScreenReadersContent>
                </LinkSimple>
              )}
            </Box>
            <RenderContentWrapper>
              <Translation
                model={{ user, type: 'comment', id: comment.id }}
                attribute="content_html"
                renderer={({ isOpen, toggle, translation }) =>
                  renderContent({
                    isOpen,
                    toggle,
                    translatedContentHtml: isOpen ? translation : undefined,
                  })
                }
              >
                {renderContent()}
              </Translation>
            </RenderContentWrapper>
          </Box>
          {isModalImagePreviewOpen && (
            <ImagePreviewModal
              images={attachedImagesPreview}
              isOpen={isModalImagePreviewOpen}
              handleCloseModal={handleCloseModalImagePreview}
              fitToScreen
            />
          )}
          {isUsersModalOpen && (
            <UsersModal
              onCloseModal={() => setIsUsersModalOpen(false)}
              routes={{
                fetchUsers: Routes.user_likes_path(currentUser.id, {
                  likeable_id: comment.id,
                  likeable_type: 'Comment',
                }),
              }}
              title={I18n.t('web.social_header.users_who_like_it')}
              responseCallback={(response) => ({
                users: response.likes.map((like) => like.user),
                total: response.likes.length,
              })}
              screenReadersCloseButtonText={s__(
                'LikesAccessibility|Close the users who like it list window',
              )}
              userNameFormat={fullName}
            />
          )}
        </>
      )}
    </Box>
  );
};

SimpleComment.defaultProps = {
  isReply: false,
  replies: [],
  Routes: { user_path: (userId) => `/users/${userId}` },
};

export { SimpleComment };

// We force "text-decoration: none;" to be consistent in all modules (i.e. Projects)
const LinkSimpleWrapper = styled(LinkSimple)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const UserDataWrapper = styled(Box).attrs({
  overflow: 'hidden',
})``;

const ImgStyled = styled(Image)`
  display: block;
  align-self: flex-start;
  border-radius: 10px;
  max-width: 100px;
  min-width: 50px;
  width: auto;
  height: 80px;
  overflow: hidden;
  cursor: pointer;
  object-fit: contain;
`;

const RenderContentWrapper = styled(Box).attrs(() => ({
  pl: 2,
}))`
  overflow: hidden;
  flex-direction: column;
  width: 100%;
`;

const CommentWrapper = styled(Box).attrs(() => ({
  p: 2,
  bg: 'smoke',
}))`
  width: 100%;
  flex-direction: column;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;

const CommentTimeStamp = styled(Box).attrs(() => ({
  pl: 3,
  position: 'relative',
}))`
  white-space: nowrap;
  &:before {
    content: '·';
    color: ${({ theme }) => theme.colors.black};
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
    position: absolute;
  }
`;

const ContentWithDotBefore = styled(Box).attrs(() => ({
  pl: 4,
  position: 'relative',
}))`
  &:before {
    content: '·';
    position: absolute;
    left: 6px;
  }
`;

const ContentWithDotAfter = styled(Box)`
  ${({ $enabled }) =>
    $enabled &&
    css`
      padding-right: 16px;
      position: relative;

      &:after {
        content: '·';
        position: absolute;
        right: 6px;
      }
    `}
`;

// We observe that the "quill editor" content is always in a <p></p> block.
const CommentContentWrapper = styled(Box).attrs(() => ({
  className: 'ckeditor-content--reset',
  display: 'block',
  pt: '2px',
}))`
  overflow: hidden;
  word-wrap: break-word;
  line-height: 1.6;
  flex-direction: column;

  ${linkEffectForChildren}

  /* Note: For a long text with multiple paragraphs (coming from a copy/paste action)
  - We remove the margin of the empty <p></p> blocks.
  - We apply a specific margin-bottom for each <p></p> blocks. */
  p:empty {
    margin: 0;
  }
  p {
    margin-bottom: 4px;
  }
`;

const CommentFakeLink = styled(Box).attrs(() => ({
  font: 'body-semibold-asphalt',
  fontSize: 12,
  fontWeight: '800',
}))`
  cursor: pointer;
  &:hover {
    text-decoration: underline 2px;
  }
`;

const CommentLikesFakeLink = styled(CommentFakeLink)`
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'default')};
  &:hover {
    text-decoration: ${({ $enabled }) => ($enabled ? 'underline 2px' : 'none')};
  }
`;

const LinkCounterLink = styled(FakeLink).attrs(() => ({
  justifyContent: 'center',
  alignItems: 'center',
}))`
  display: flex;
  &:hover {
    text-decoration: none;
    cursor: ${({ $count }) => ($count === 0 ? 'default' : 'pointer')};
  }
`;

const UserName = styled(Box).attrs(() => ({
  display: 'block',
  flexGrow: 1,
  font: 'body-semibold-asphalt',
}))`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
